import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useJobs } from '@/hooks/useJobs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Loader2, Search, ExternalLink, RefreshCw } from 'lucide-react';
import { format } from 'date-fns';
import { JobStatus, JOB_CATEGORIES, JobCategory } from '@/lib/types/job';

const JOB_STATUS_OPTIONS: { value: JobStatus; label: string }[] = [
  { value: 'draft', label: 'Draft' },
  { value: 'rfq_open', label: 'RFQ Open' },
  { value: 'awarded', label: 'Awarded' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed_pending_review', label: 'Pending Review' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'disputed', label: 'Disputed' },
  { value: 'closed', label: 'Closed' },
];

const getStatusLabel = (status: string): string => {
  const found = JOB_STATUS_OPTIONS.find(s => s.value === status);
  return found?.label || status;
};

const getCategoryLabel = (category: string): string => {
  const found = JOB_CATEGORIES.find(c => c.value === category);
  return found?.label || category;
};

export default function AdminJobsPage() {
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  const { jobs, loading, error, refetch } = useJobs({
    status: statusFilter !== 'all' ? [statusFilter as JobStatus] : undefined,
    category: categoryFilter !== 'all' ? [categoryFilter as JobCategory] : undefined,
    includeOrganization: true,
  });

  // Client-side search filter including client and vendor names
  const filteredJobs = jobs.filter(job => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    
    // Get client name from creator profile
    const clientName = (job as any).creator 
      ? `${(job as any).creator.first_name || ''} ${(job as any).creator.last_name || ''}`.toLowerCase() 
      : '';
    
    // Get vendor name from purchase order's vendor organization
    const vendorOrgName = (job as any).purchase_order?.vendor_organization?.name?.toLowerCase() || '';
    const vendorBusinessName = (job as any).purchase_order?.vendor_organization?.vendor_profile?.[0]?.business_name?.toLowerCase() || '';
    
    return (
      job.title?.toLowerCase().includes(query) ||
      job.public_id?.toLowerCase().includes(query) ||
      job.organization?.name?.toLowerCase().includes(query) ||
      clientName.includes(query) ||
      vendorOrgName.includes(query) ||
      vendorBusinessName.includes(query)
    );
  });

  const getStatusBadgeVariant = (status: string): "default" | "secondary" | "destructive" | "outline" => {
    switch (status) {
      case 'draft': return 'secondary';
      case 'rfq_open': return 'default';
      case 'awarded': return 'default';
      case 'in_progress': return 'default';
      case 'completed_pending_review': return 'secondary';
      case 'accepted': return 'default';
      case 'rejected': return 'destructive';
      case 'disputed': return 'destructive';
      case 'closed': return 'outline';
      default: return 'secondary';
    }
  };

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">All Jobs</h1>
          <p className="text-muted-foreground">View and manage all jobs across all organizations</p>
        </div>
        <Button variant="outline" onClick={refetch} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-lg">Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by title, ID, organization, client, or vendor..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {JOB_STATUS_OPTIONS.map(({ value, label }) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {JOB_CATEGORIES.map(({ value, label }) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <div className="text-center py-12 text-destructive">
              <p>Error loading jobs: {error}</p>
              <Button variant="outline" onClick={refetch} className="mt-4">
                Try Again
              </Button>
            </div>
          ) : filteredJobs.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <p>No jobs found matching your filters.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Job ID</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Organization</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredJobs.map((job) => (
                  <TableRow key={job.id}>
                    <TableCell className="font-mono text-sm">
                      {job.public_id?.slice(0, 8)}...
                    </TableCell>
                    <TableCell className="font-medium max-w-[250px] truncate">
                      {job.title}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {job.organization?.name || 'Unknown'}
                    </TableCell>
                    <TableCell>
                      {getCategoryLabel(job.category)}
                    </TableCell>
                    <TableCell>
                      <Badge variant={getStatusBadgeVariant(job.status)}>
                        {getStatusLabel(job.status)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {job.created_at ? format(new Date(job.created_at), 'MMM d, yyyy') : '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" asChild>
                        <Link to={`/jobs/${job.public_id}`}>
                          <ExternalLink className="h-4 w-4" />
                        </Link>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <div className="mt-4 text-sm text-muted-foreground">
        Showing {filteredJobs.length} of {jobs.length} jobs
      </div>
    </div>
  );
}
